import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import "../App.css";

const MODULE_OPTIONS = [
  "upload",
  "parsing",
  "completeness",
  "course_execution",
  "clo_alignment",
  "grading",
  "quality",
  "feedback",
  "reminder",
  "admin_documents",
  "system",
];

const SEVERITY_OPTIONS = ["low", "medium", "high", "critical"];

const badge = (type, value) => {
  const v = (value || "").toLowerCase();

  const styles = {
    critical: ["#fee2e2", "#991b1b"],
    high: ["#ffedd5", "#9a3412"],
    medium: ["#fef9c3", "#854d0e"],
    low: ["#dcfce7", "#166534"],
    open: ["#fee2e2", "#991b1b"],
    resolved: ["#dcfce7", "#166534"],
    ignored: ["#e5e7eb", "#374151"],
  };

  const [bg, fg] = styles[v] || ["#e0f2fe", "#075985"];

  return {
    display: "inline-flex",
    padding: "5px 10px",
    borderRadius: 999,
    background: bg,
    color: fg,
    fontWeight: 800,
    fontSize: 12,
    textTransform: "capitalize",
  };
};

function getBackendError(e, fallback) {
  const detail = e?.response?.data?.detail;

  if (Array.isArray(detail)) {
    return detail.map((item) => item?.msg || JSON.stringify(item)).join("\n");
  }

  if (typeof detail === "string") return detail;

  return fallback;
}

function formatModuleName(value) {
  return (value || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export default function AdminQAControlPanel() {
  const [exceptions, setExceptions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [moduleStats, setModuleStats] = useState([]);

  const [statusFilter, setStatusFilter] = useState("open");
  const [moduleFilter, setModuleFilter] = useState("");
  const [severityFilter, setSeverityFilter] = useState("");

  const [manualForm, setManualForm] = useState({
    course_id: "",
    upload_id: "",
    module: "upload",
    error_type: "Manual QA Issue",
    message: "",
    severity: "medium",
  });

  const [seedCourseId, setSeedCourseId] = useState("");

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState("");

  const loadData = async () => {
    setErr("");
    setLoading(true);

    try {
      const params = {};

      if (statusFilter) params.status = statusFilter;
      if (moduleFilter) params.module = moduleFilter;
      if (severityFilter) params.severity = severityFilter;

      const [listRes, summaryRes, moduleRes] = await Promise.all([
        api.get("/exceptions/", { params }),
        api.get("/exceptions/summary"),
        api.get("/exceptions/modules"),
      ]);

      setExceptions(listRes.data || []);
      setSummary(summaryRes.data || null);
      setModuleStats(moduleRes.data || []);
    } catch (e) {
      setErr(getBackendError(e, "Failed to load QA control panel."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [statusFilter, moduleFilter, severityFilter]);

  const updateStatus = async (id, status) => {
    setErr("");
    setSuccess("");

    try {
      await api.patch(`/exceptions/${id}/status`, { status });
      setSuccess(`Exception marked as ${status}.`);
      await loadData();
    } catch (e) {
      setErr(getBackendError(e, "Failed to update exception status."));
    }
  };

  const createManualException = async (e) => {
    e.preventDefault();

    setErr("");
    setSuccess("");

    if (!manualForm.error_type.trim()) {
      setErr("Error type is required.");
      return;
    }

    if (!manualForm.message.trim()) {
      setErr("Message is required.");
      return;
    }

    setCreating(true);

    try {
      await api.post("/exceptions/", {
        course_id: manualForm.course_id.trim() || null,
        upload_id: manualForm.upload_id.trim() || null,
        module: manualForm.module,
        error_type: manualForm.error_type.trim(),
        message: manualForm.message.trim(),
        severity: manualForm.severity,
      });

      setSuccess("Manual QA exception created successfully.");

      setManualForm({
        course_id: "",
        upload_id: "",
        module: "upload",
        error_type: "Manual QA Issue",
        message: "",
        severity: "medium",
      });

      setStatusFilter("open");
      await loadData();
    } catch (e2) {
      setErr(getBackendError(e2, "Failed to create manual exception."));
    } finally {
      setCreating(false);
    }
  };

  const seedTestExceptions = async () => {
    setErr("");
    setSuccess("");
    setSeeding(true);

    try {
      const res = await api.post("/exceptions/test-seed", {
        course_id: seedCourseId.trim() || null,
      });

      setSuccess(res?.data?.message || "Test exceptions created successfully.");
      setStatusFilter("open");
      await loadData();
    } catch (e) {
      setErr(getBackendError(e, "Failed to seed test exceptions."));
    } finally {
      setSeeding(false);
    }
  };

  const availableModules = useMemo(() => {
    if (moduleStats.length) {
      return moduleStats.map((m) => m.module);
    }

    return MODULE_OPTIONS;
  }, [moduleStats]);

  return (
    <div className="fade-in">
      <div className="page-header">
        <h1 className="page-title">Admin QA Control Panel</h1>
        <p className="page-subtitle">
          Monitor system exceptions, fallback cases, parsing failures, quality
          issues, and manual QA actions.
        </p>
      </div>

      {err && (
        <div
          className="card"
          style={{
            padding: 14,
            marginBottom: 16,
            background: "#fff1f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            fontWeight: 800,
            whiteSpace: "pre-line",
          }}
        >
          {err}
        </div>
      )}

      {success && (
        <div
          className="card"
          style={{
            padding: 14,
            marginBottom: 16,
            background: "#ecfdf5",
            border: "1px solid #bbf7d0",
            color: "#166534",
            fontWeight: 800,
          }}
        >
          {success}
        </div>
      )}

      <div className="dashboard-grid" style={{ marginBottom: 24 }}>
        <div className="stats-card">
          <div className="stats-number">{summary?.total ?? "—"}</div>
          <div className="stats-label">Total Exceptions</div>
        </div>

        <div className="stats-card">
          <div className="stats-number">{summary?.open ?? "—"}</div>
          <div className="stats-label">Open</div>
        </div>

        <div className="stats-card">
          <div className="stats-number">{summary?.critical ?? "—"}</div>
          <div className="stats-label">Critical</div>
        </div>

        <div className="stats-card">
          <div className="stats-number">{summary?.resolved ?? "—"}</div>
          <div className="stats-label">Resolved</div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h2 className="card-title">Test QA Issues</h2>
        </div>

        <div className="card-content">
          <p style={{ color: "#64748b", marginTop: 0 }}>
            Use this during testing or viva demo to populate the QA Control Panel
            with realistic exception records.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 12,
              alignItems: "center",
            }}
          >
            <input
              className="form-input"
              placeholder="Optional Course ID"
              value={seedCourseId}
              onChange={(e) => setSeedCourseId(e.target.value)}
            />

            <button
              className="btn btn-primary"
              type="button"
              onClick={seedTestExceptions}
              disabled={seeding}
            >
              {seeding ? "Creating..." : "Generate Test Exceptions"}
            </button>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h2 className="card-title">Create Manual QA Exception</h2>
        </div>

        <div className="card-content">
          <form onSubmit={createManualException}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                marginBottom: 12,
              }}
            >
              <input
                className="form-input"
                placeholder="Optional Course ID"
                value={manualForm.course_id}
                onChange={(e) =>
                  setManualForm({ ...manualForm, course_id: e.target.value })
                }
              />

              <input
                className="form-input"
                placeholder="Optional Upload ID"
                value={manualForm.upload_id}
                onChange={(e) =>
                  setManualForm({ ...manualForm, upload_id: e.target.value })
                }
              />

              <select
                className="form-input"
                value={manualForm.module}
                onChange={(e) =>
                  setManualForm({ ...manualForm, module: e.target.value })
                }
              >
                {MODULE_OPTIONS.map((m) => (
                  <option key={m} value={m}>
                    {formatModuleName(m)}
                  </option>
                ))}
              </select>

              <select
                className="form-input"
                value={manualForm.severity}
                onChange={(e) =>
                  setManualForm({ ...manualForm, severity: e.target.value })
                }
              >
                {SEVERITY_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s.toUpperCase()}
                  </option>
                ))}
              </select>

              <input
                className="form-input"
                placeholder="Error Type"
                value={manualForm.error_type}
                onChange={(e) =>
                  setManualForm({ ...manualForm, error_type: e.target.value })
                }
              />
            </div>

            <textarea
              className="form-input"
              placeholder="Describe the QA issue..."
              value={manualForm.message}
              onChange={(e) =>
                setManualForm({ ...manualForm, message: e.target.value })
              }
              style={{
                minHeight: 90,
                resize: "vertical",
                marginBottom: 12,
              }}
            />

            <button
              className="btn btn-primary"
              type="submit"
              disabled={creating}
            >
              {creating ? "Creating..." : "Create Manual Exception"}
            </button>
          </form>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h2 className="card-title">Filters</h2>
        </div>

        <div
          className="card-content"
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr auto",
            gap: 12,
            alignItems: "center",
          }}
        >
          <select
            className="form-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="resolved">Resolved</option>
            <option value="ignored">Ignored</option>
          </select>

          <select
            className="form-input"
            value={moduleFilter}
            onChange={(e) => setModuleFilter(e.target.value)}
          >
            <option value="">All Modules</option>
            {availableModules.map((m) => (
              <option key={m} value={m}>
                {formatModuleName(m)}
              </option>
            ))}
          </select>

          <select
            className="form-input"
            value={severityFilter}
            onChange={(e) => setSeverityFilter(e.target.value)}
          >
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <button className="btn btn-secondary" onClick={loadData}>
            Refresh
          </button>
        </div>
      </div>

      {moduleStats.length > 0 && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">
            <h2 className="card-title">Module Issue Breakdown</h2>
          </div>

          <div
            className="card-content"
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            {moduleStats.map((item) => (
              <span
                key={item.module}
                style={{
                  padding: "8px 12px",
                  borderRadius: 999,
                  border: "1px solid #e2e8f0",
                  background: item.count > 0 ? "#fef2f2" : "#f8fafc",
                  color: item.count > 0 ? "#991b1b" : "#475569",
                  fontWeight: 800,
                  fontSize: 13,
                }}
              >
                {formatModuleName(item.module)}: {item.count}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Exception Logbook</h2>
        </div>

        <div className="card-content">
          {loading ? (
            <div style={{ color: "#64748b" }}>Loading exceptions...</div>
          ) : exceptions.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: "center",
                color: "#64748b",
                background: "#f8fafc",
                border: "1px dashed #cbd5e1",
                borderRadius: 14,
              }}
            >
              No exceptions found for the selected filters.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 14,
                }}
              >
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    <th style={th}>Module</th>
                    <th style={th}>Error Type</th>
                    <th style={th}>Message</th>
                    <th style={th}>Severity</th>
                    <th style={th}>Status</th>
                    <th style={th}>Course ID</th>
                    <th style={th}>Created</th>
                    <th style={th}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {exceptions.map((item) => (
                    <tr key={item.id}>
                      <td style={td}>{formatModuleName(item.module)}</td>
                      <td style={td}>{item.error_type}</td>
                      <td style={{ ...td, minWidth: 260 }}>{item.message}</td>
                      <td style={td}>
                        <span style={badge("severity", item.severity)}>
                          {item.severity}
                        </span>
                      </td>
                      <td style={td}>
                        <span style={badge("status", item.status)}>
                          {item.status}
                        </span>
                      </td>
                      <td style={td}>{item.course_id || "—"}</td>
                      <td style={td}>
                        {item.created_at
                          ? new Date(item.created_at).toLocaleString()
                          : "—"}
                      </td>
                      <td style={td}>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                          {item.status !== "resolved" && (
                            <button
                              className="btn btn-success"
                              style={{ padding: "6px 10px", fontSize: 12 }}
                              onClick={() => updateStatus(item.id, "resolved")}
                            >
                              Resolve
                            </button>
                          )}

                          {item.status !== "ignored" && (
                            <button
                              className="btn btn-secondary"
                              style={{ padding: "6px 10px", fontSize: 12 }}
                              onClick={() => updateStatus(item.id, "ignored")}
                            >
                              Ignore
                            </button>
                          )}

                          {item.status !== "open" && (
                            <button
                              className="btn btn-primary"
                              style={{ padding: "6px 10px", fontSize: 12 }}
                              onClick={() => updateStatus(item.id, "open")}
                            >
                              Reopen
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const th = {
  padding: "12px",
  textAlign: "left",
  borderBottom: "1px solid #e2e8f0",
  color: "#475569",
  fontWeight: 900,
};

const td = {
  padding: "12px",
  borderBottom: "1px solid #e2e8f0",
  verticalAlign: "top",
};